import { Box, Typography } from '@material-ui/core';
import { green } from '@material-ui/core/colors';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { CheckCircle, Error as ErrorIcon } from '@material-ui/icons';
import { Layout, LoginLogoImg } from '@roc/feature-app-core';
import { Paper, StepsContainer, VerticalBarLoader } from '@roc/ui';
import { observer } from 'mobx-react';
import { useEffect } from 'react';
import { useParams } from 'react-router';
import { useStore } from '@roc/client-portal-shared/hooks';
import { VerifyBankInformationForm } from './verifyBankInformationForm';
import { VerifyBankInformationSignatureStep } from './verifyBankInformationSignatureStep';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    paper: {
      padding: theme.spacing(3, 3, 4),
      minHeight: 700,
    },
    stepper: {
      maxWidth: 900,
      margin: 'auto',
      [theme.breakpoints.down('xs')]: {
        '& .MuiStepLabel-label': {
          display: 'none',
        },
        '& .MuiStepLabel-label.MuiStepLabel-active': {
          display: 'block',
        },
      },
    },
  })
);

export const VerifyBankInformationPage = observer(() => {
  const classes = useStyles();
  const { token } = useParams<{ token: string }>();
  const { verifyBankInformationStore } = useStore() as any;
  const store = verifyBankInformationStore;

  useEffect(() => {
    if (token) {
      store.fetchSigningInfo(token);
    }
  }, [token]);

  const steps = [
    {
      title: 'Bank Information',
      icon: null,
      onBack: null,
      onNext: () => {
        if (store.form.meta.isValid) {
          store.goNextStep();
        } else {
          store.runFormValidationWithMessage();
        }
      },
      allowBack: false,
      allowNext: true,
    },
    {
      title: 'Electronic Signature',
      icon: null,
      onBack: () => {
        store.goPrevStep();
      },
      onNext: () => {/**nothing to do here**/ },
      allowBack: true,
      allowNext: false,
    },
  ];

  return (
    <Layout maxWidth="lg">
      <Box display="flex" justifyContent="center">
        <LoginLogoImg imgStyle={{ width: '150px' }} />
      </Box>
      <Box my={3}>
        <Typography variant="h3" gutterBottom>
          {'Verify Bank Information'}
        </Typography>
      </Box>
      {store.loading && <VerticalBarLoader />}
      <Paper className={classes.paper}>
        {store.linkInvalid && <InvalidLinkView />}
        {!store.linkInvalid && store.completed && <CompletedView />}
        {!store.linkInvalid && !store.completed && (
          <StepsContainer
            activeStep={store.activeStep}
            disableNavigation={true}
            steps={steps}
            stepperClassName={classes.stepper}
          >
            {store.activeStep === 0 && <VerifyBankInformationForm store={store} />}
            {store.activeStep === 1 && <VerifyBankInformationSignatureStep store={store} />}
          </StepsContainer>
        )}
      </Paper>
    </Layout>
  );
});

const CompletedView = () => (
  <Box mt={4} mx={'auto'} textAlign="center">
    <CheckCircle style={{ fontSize: 60, color: green[500] }} />
    <br />
    <Typography variant="h4" color="textPrimary">
      Thank you
    </Typography>
    <br />
    <Typography>Your bank information has been submitted successfully</Typography>
    <br />
  </Box>
);

const InvalidLinkView = () => (
  <Box mt={4} mx={'auto'} textAlign="center">
    <ErrorIcon style={{ fontSize: 60 }} color="error" />
    <br />
    <Typography variant="h4" color="textPrimary">
      This link is no longer valid
    </Typography>
    <br />
    <Typography>
      Please contact your Roc Capital representative to request a new bank information link.
    </Typography>
    <br />
  </Box>
);
